import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Global custom cursor. Uses [data-cursor] attribute on elements to switch state.
 * Supported: default | hover | drag | view | send
 */
const LABELS_TR = {
  view: "gör",
  open: "aç",
  "say hi": "selam",
  send: "gönder",
  mail: "posta",
  go: "git",
  hover: "",
  drag: "sürükle",
};

export default function CustomCursor() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 500, damping: 40, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 500, damping: 40, mass: 0.4 });

  const [label, setLabel] = useState("");
  const [variant, setVariant] = useState("default");
  const [visible, setVisible] = useState(false);
  const isTouch = useRef(false);

  useEffect(() => {
    isTouch.current =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (isTouch.current) return;

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      if (!visible) setVisible(true);
    };
    const over = (e) => {
      const el = e.target?.closest?.("[data-cursor]");
      if (el) {
        setVariant(el.getAttribute("data-cursor") || "hover");
        const raw = el.getAttribute("data-cursor-label") || "";
        setLabel(raw in LABELS_TR ? LABELS_TR[raw] : raw);
      } else {
        setVariant("default");
        setLabel("");
      }
    };
    const leave = () => setVisible(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    document.addEventListener("mouseleave", leave);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      document.removeEventListener("mouseleave", leave);
    };
  }, [x, y, visible]);

  if (isTouch.current) return null;

  const size =
    variant === "hover" || variant === "view" || variant === "send"
      ? 84
      : variant === "drag"
      ? 68
      : 14;

  const bg =
    variant === "hover"
      ? "var(--cobalt)"
      : variant === "view"
      ? "var(--coral)"
      : variant === "send"
      ? "var(--chartreuse)"
      : variant === "drag"
      ? "var(--ink)"
      : "var(--ink)";

  const color =
    variant === "view"
      ? "var(--ink)"
      : variant === "send"
      ? "var(--ink)"
      : "var(--bone)";

  return (
    <>
      <style>{`
        @media (pointer: fine) {
          * { cursor: none !important; }
        }
      `}</style>
      <motion.div
        aria-hidden
        data-testid="custom-cursor"
        style={{
          translateX: sx,
          translateY: sy,
          width: size,
          height: size,
          background: bg,
          color,
          opacity: visible ? 1 : 0,
        }}
        animate={{ width: size, height: size, background: bg }}
        transition={{ type: "spring", stiffness: 260, damping: 24, mass: 0.5 }}
        className="fixed left-0 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full z-[110] pointer-events-none flex items-center justify-center mix-blend-difference"
      >
        {label && (
          <span
            className="font-mono-label text-[10px] uppercase tracking-widest select-none"
            style={{ color }}
          >
            {label}
          </span>
        )}
      </motion.div>
    </>
  );
}
